/**
 * Iter88zbs (2026-02-20) — Konsolidierte Produkt-Katalog-Verwaltung.
 *
 * Führt die 5 Admin-Seiten in ONE Zugang zusammen:
 *   /admin/produkte?tab=pv-katalog      → AdminPvCatalog
 *   /admin/produkte?tab=pv-pakete       → AdminPvPackages
 *   /admin/produkte?tab=wp-katalog      → AdminWpCatalog
 *   /admin/produkte?tab=wp-komponenten  → AdminWpComponents
 *   /admin/produkte?tab=wp-pakete       → AdminWpPackages
 *
 * Die alten Routen (`/admin/pv-catalog`, `/admin/wp-catalog`, …) bleiben als
 * Weiterleitungen erhalten — keine Broken-Links, keine Regressionen. Die
 * Sub-Seiten selbst werden mit `embedded={true}` gerendert, sodass ihre
 * eigenen `<AppHeader>`-Aufrufe unterdrückt werden und die Tab-Shell den
 * einzigen Header liefert.
 *
 * PV=WP-Symmetrie wird sichtbar: linke Gruppe „Photovoltaik" · rechte Gruppe
 * „Wärmepumpe" — beide mit identischer Struktur „Katalog | Pakete".
 */
import { useMemo } from "react";
import { useSearchParams } from "react-router-dom";
import { Sun, Flame, Boxes, Package } from "lucide-react";
import { AppHeader } from "@/components/AppHeader";
import AdminPvCatalog from "@/pages/AdminPvCatalog";
import AdminPvPackages from "@/pages/AdminPvPackages";
import AdminWpCatalog from "@/pages/AdminWpCatalog";
import AdminWpComponents from "@/pages/AdminWpComponents";
import AdminWpPackages from "@/pages/AdminWpPackages";

const TABS = [
  {
    id: "pv-katalog",
    label: "PV-Komponenten",
    group: "pv",
    icon: Boxes,
    render: () => <AdminPvCatalog embedded />,
  },
  {
    id: "pv-pakete",
    label: "PV-Pakete",
    group: "pv",
    icon: Package,
    render: () => <AdminPvPackages embedded />,
  },
  // Iter88zz9q — WP-Hersteller-Tab entfernt. Alle WP-Modelle liegen jetzt in
  // WP-Komponenten (Kategorie „hardware") inkl. kW-Bracket-Range für den
  // Konfigurator. Daten bleiben in `heat_pump_products` als Backup, sind aber
  // nicht mehr über die UI erreichbar.
  {
    id: "wp-komponenten",
    label: "WP-Komponenten",
    group: "wp",
    icon: Boxes,
    render: () => <AdminWpComponents embedded />,
  },
  {
    id: "wp-pakete",
    label: "WP-Pakete",
    group: "wp",
    icon: Package,
    render: () => <AdminWpPackages embedded />,
  },
];

export default function AdminProdukte() {
  const [params, setParams] = useSearchParams();
  const currentId = params.get("tab") || "pv-katalog";
  const current = useMemo(() => TABS.find((t) => t.id === currentId) || TABS[0], [currentId]);

  const setTab = (id) => {
    params.set("tab", id);
    setParams(params, { replace: true });
  };

  const TabButton = ({ tab }) => {
    const Icon = tab.icon;
    const active = tab.id === current.id;
    return (
      <button
        onClick={() => setTab(tab.id)}
        data-testid={`admin-produkte-tab-${tab.id}`}
        className={`h-9 px-3 rounded-lg text-[12px] font-semibold inline-flex items-center gap-1.5 transition ${
          active
            ? "bg-[#00E575] text-[#09090B]"
            : "bg-[#18181B] border border-zinc-800 text-zinc-300 hover:border-zinc-600"
        }`}>
        <Icon size={13}/> {tab.label}
      </button>
    );
  };

  const pvTabs = TABS.filter((t) => t.group === "pv");
  const wpTabs = TABS.filter((t) => t.group === "wp");

  return (
    <div className="min-h-screen bg-[#09090B]">
      <AppHeader title="Produkte" back="/dashboard" />

      {/* Symmetrische Tab-Bar: PV links, WP rechts, gleicher Aufbau */}
      <div className="max-w-6xl mx-auto px-4 pt-4">
        <div className="flex flex-wrap gap-3 items-center" data-testid="admin-produkte-tabs">
          <div className="flex items-center gap-1.5">
            <span className="text-[10.5px] uppercase tracking-widest text-amber-300/80 font-bold inline-flex items-center gap-1">
              <Sun size={12}/> Photovoltaik
            </span>
            <div className="flex gap-1.5 ml-1">
              {pvTabs.map((t) => <TabButton key={t.id} tab={t}/>)}
            </div>
          </div>

          <div className="w-px h-6 bg-zinc-800 mx-1 hidden sm:block"/>

          <div className="flex items-center gap-1.5">
            <span className="text-[10.5px] uppercase tracking-widest text-orange-400/80 font-bold inline-flex items-center gap-1">
              <Flame size={12}/> Wärmepumpe
            </span>
            <div className="flex gap-1.5 ml-1">
              {wpTabs.map((t) => <TabButton key={t.id} tab={t}/>)}
            </div>
          </div>
        </div>
      </div>

      {/* Aktive Sub-Seite (embedded=true → kein zweiter AppHeader) */}
      <div key={current.id} data-testid={`admin-produkte-content-${current.id}`}>
        {current.render()}
      </div>
    </div>
  );
}
